import { io, Socket } from 'socket.io-client-v4';
import { ISocketService, SocketConfig, SocketAckResponse } from './socket.types';

class SocketV2Service implements ISocketService {
  private static instance: SocketV2Service;
  private socket: Socket | null = null;
  private config: SocketConfig;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private listeners: Map<string, Array<(...args: any[]) => void>> = new Map();

  private constructor(config: SocketConfig) {
    this.config = config;
  }

  static getInstance(config?: SocketConfig): SocketV2Service {
    if (!SocketV2Service.instance) {
      if (!config) {
        throw new Error('[SocketV2] Config is required for first initialization');
      }
      console.log('[SocketV2Service] Criando NOVA instância');
      SocketV2Service.instance = new SocketV2Service(config);
    }
    return SocketV2Service.instance;
  }

  static hasInstance(): boolean {
    return SocketV2Service.instance !== null && SocketV2Service.instance !== undefined;
  }

  static resetInstance(): void {
    if (SocketV2Service.instance) {
      SocketV2Service.instance.disconnect();
      SocketV2Service.instance = null as any;
    }
  }

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    // Socket anterior desconectado continua tentando reconectar sozinho
    // (`reconnection: true`) com todos os handlers ainda ligados. Sem descartá-lo
    // aqui, o `io()` abaixo criaria um segundo socket: os dois entram na mesma
    // sala do evento e cada `recorded` chega em dobro.
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    try {
      const defaultOptions = {
        path: '/ws/',
        transports: ['websocket', 'polling'] as Array<'websocket' | 'polling'>,
      };
      this.socket = io(this.config.url, {
        ...defaultOptions,
        ...this.config.options,
      });

      this.setupDefaultListeners();
      this.reattachListeners();
      
    } catch (error) {
      console.error('[SocketV2] Connection error:', error);
      throw error;
    }
  }

  private setupDefaultListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
    });


    this.socket.on('disconnect', (reason: string) => {
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      this.reconnectAttempts++;
      console.error(
        `[SocketV2] ❌ Connection error (${this.reconnectAttempts}/${this.maxReconnectAttempts}):`,
        error.message
      );

      // Tratamento específico de erros de autenticação e validação
      if (error.message.includes('Authentication error')) {
        console.error('[SocketV2] 🔒 Authentication failed - check token');
      } else if (error.message.includes('Validation error')) {
        console.error('[SocketV2] 🚫 Validation failed - check eventId');
      } else if (error.message.includes('Authorization error')) {
        console.error('[SocketV2] 🚫 Authorization failed - user not allowed for this event');
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[SocketV2] ⛔ Max reconnection attempts reached');
      }
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      this.reconnectAttempts = 0;
      this.reattachListeners();
    });

    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
    });

    this.socket.on('reconnect_error', (error: Error) => {
      console.error('[SocketV2] ❌ Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[SocketV2] ⛔ Reconnection failed after max attempts');
    });

    this.socket.on('error', (error: Error | string) => {
      console.error('[SocketV2] ❌ Socket error:', error);
    });
  }

  private reattachListeners(): void {
    if (!this.socket) return;

    this.listeners.forEach((handlers, event) => {
      handlers.forEach(handler => {
        this.socket?.on(event, handler);
      });
    });
  }

  /**
   * Troca o token da conexão. O socket.io só envia `auth` no handshake, então
   * a credencial nova só vale depois de reconectar — daí o ciclo explícito.
   * No-op quando o token não mudou, para não derrubar a conexão à toa.
   */
  updateAuth(token: string): void {
    if (!token || this.config.options?.auth?.token === token) return;

    this.config = {
      ...this.config,
      options: {
        ...this.config.options,
        auth: { ...this.config.options?.auth, token },
      },
    };

    if (!this.socket) return;

    (this.socket as any).auth = { ...(this.socket as any).auth, token };
    if (this.socket.connected) {
      this.socket.disconnect().connect();
    } else {
      this.socket.connect();
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  emit(event: string, ...args: any[]): void {
    if (!this.socket) {
      console.warn('[SocketV2] Cannot emit - socket not initialized');
      return;
    }

    if (!this.socket.connected) {
      console.warn('[SocketV2] Cannot emit - not connected. Event:', event);
      return;
    }

    this.socket.emit(event, ...args);
  }

  emitWithAck<T = any>(
    event: string,
    data: any,
    timeout: number = 5000
  ): Promise<SocketAckResponse<T>> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('[SocketV2] Socket not initialized'));
        return;
      }

      if (!this.socket.connected) {
        reject(new Error('[SocketV2] Socket not connected'));
        return;
      }

      this.socket.timeout(timeout).emit(event, data, (error: Error, response: any) => {
        if (error) {
          const isTimeout = error.message.includes('timeout') || error.message.includes('timed out');
          const isDisconnected = error.message.includes('disconnected') || error.message.includes('disconnect');
          
          if (isTimeout) {
            console.warn(`[SocketV2] Emit with ack timeout for event "${event}" after ${timeout}ms`);
          } else if (!isDisconnected) {
            // Não logar erros de desconexão (esperados durante logout)
            console.error(`[SocketV2] Emit with ack error for event "${event}":`, error);
          }
          
          resolve({
            success: false,
            error: error.message,
          });
        } else {
          resolve({
            success: true,
            data: response,
          });
        }
      });
    });
  }

  on<T = any>(event: string, handler: (data: T) => void): void {
    if (!this.socket) {
      console.warn('[SocketV2] Cannot add listener - socket not initialized');
      return;
    }

    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(handler);

    this.socket.on(event, handler);
  }

  once<T = any>(event: string, handler: (data: T) => void): void {
    if (!this.socket) {
      console.warn('[SocketV2] Cannot add one-time listener - socket not initialized');
      return;
    }

    this.socket.once(event, handler);
  }

  off(event: string, handler?: (...args: any[]) => void): void {
    if (!this.socket) return;

    if (handler) {
      this.socket.off(event, handler);
      
      const handlers = this.listeners.get(event);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
        if (handlers.length === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      this.socket.off(event);
      this.listeners.delete(event);
    }
  }

  removeAllListeners(event?: string): void {
    if (!this.socket) return;

    if (event) {
      this.socket.removeAllListeners(event);
      this.listeners.delete(event);
    } else {
      this.socket.removeAllListeners();
      this.listeners.clear();
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // Métodos auxiliares para facilitar uso comum
  joinRoom(room: string): void {
    this.emit('join', room);
  }

  leaveRoom(room: string): void {
    this.emit('leave', room);
  }

  async joinRoomWithAck(room: string, timeout: number = 10000): Promise<SocketAckResponse> {
    return this.emitWithAck('join', { room }, timeout);
  }

  /**
   * Entra na sala do evento pelo id do evento. Preferir a `joinRoomWithAck`:
   * o servidor resolve o nome da sala e valida a autorização, e só por este
   * caminho o scout entra também na sala privada de scouts.
   */
  async joinEventWithAck(
    eventId: string,
    timeout: number = 10000,
  ): Promise<SocketAckResponse> {
    return this.emitWithAck('join', { eventId }, timeout);
  }

  async leaveRoomWithAck(room: string, timeout: number = 10000): Promise<SocketAckResponse> {
    return this.emitWithAck('leave', { room }, timeout);
  }
}

export default SocketV2Service;

